/**
 * Permissions Context
 *
 * Manages user permissions for feature access control.
 * Uses React Query to leverage bootstrap cache - no duplicate API calls.
 */

import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { getCurrentUserProfile, type AdminUser } from '@/lib/api';
import { type UserPermissions, DEFAULT_PERMISSIONS } from '@/types/admin';
import { queryKeys } from '@/lib/queryKeys';

interface PermissionsContextType {
  permissions: UserPermissions;
  isLoading: boolean;
  error: string | null;
  userProfile: AdminUser | null;
  hasPermission: (feature: keyof UserPermissions, action: string) => boolean;
  isAdmin: boolean;  // User has admin or owner role
  canReadCRM: boolean;
  canCreateCRM: boolean;
  canUpdateCRM: boolean;
  canDeleteCRM: boolean;
  canReadAnalytics: boolean;
  canReadAdmin: boolean;
  canManageUsers: boolean;
  canManageSettings: boolean;
  canReadProjects: boolean;
  canCreateProjects: boolean;
  canUpdateProjects: boolean;
  canDeleteProjects: boolean;
  // Hiring permissions
  canReadHiring: boolean;
  canCreateHiring: boolean;
  canUpdateHiring: boolean;
  canDeleteHiring: boolean;
  // Forms permissions
  canReadForms: boolean;
  canCreateForms: boolean;
  canUpdateForms: boolean;
  canDeleteForms: boolean;
  // Portal permissions
  canReadPortal: boolean;
  canManagePortal: boolean;
  canPortalStaffAccess: boolean;  // Staff login with client switching
  // Team Hub permissions
  canReadTeamHub: boolean;
  canCreateTeamHub: boolean;
  canUpdateTeamHub: boolean;
  canDeleteTeamHub: boolean;
  // Customer Success permissions
  canReadCustomerSuccess: boolean;
  canManageCustomerSuccess: boolean;
  // Production permissions
  canReadProduction: boolean;
  canCreateProduction: boolean;
  canUpdateProduction: boolean;
  canDeleteProduction: boolean;
  // Agency Intel permissions
  canReadAgencyIntel: boolean;
  // LiquorChat permissions
  canReadLiquorChat: boolean;
  canManageLiquorChat: boolean;
  // Ads permissions
  canReadAds: boolean;
  canManageAds: boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export function PermissionsProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  // Use React Query to fetch user profile - benefits from bootstrap cache seeding
  // Bootstrap seeds queryKeys.user.me() so this will often return cached data immediately
  const {
    data: userProfile,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: queryKeys.user.me(),
    queryFn: getCurrentUserProfile,
    enabled: isAuthenticated && !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes - permissions don't change often
    gcTime: 30 * 60 * 1000, // 30 minutes cache retention
    refetchOnMount: false, // Don't refetch if data exists (from bootstrap)
    refetchOnWindowFocus: false,
  });

  // Extract permissions from user profile, fall back to role-based or defaults
  // Merge role defaults with stored permissions so new permission keys (e.g. liquorchat)
  // automatically get their role-based defaults even if stored permissions pre-date them.
  const permissions = useMemo(() => {
    if (!userProfile) return DEFAULT_PERMISSIONS;
    const roleDefaults = getRoleBasedPermissions(userProfile.role);
    if (userProfile.permissions) {
      return { ...roleDefaults, ...userProfile.permissions };
    }
    return roleDefaults;
  }, [userProfile]);

  const error = queryError ? (queryError instanceof Error ? queryError.message : 'Failed to load permissions') : null;

  // Permission check helper
  const hasPermission = useCallback(
    (feature: keyof UserPermissions, action: string): boolean => {
      const featurePerms = permissions[feature];
      if (!featurePerms) return false;
      return (featurePerms as unknown as Record<string, boolean>)[action] ?? false;
    },
    [permissions]
  );

  // Manual refresh function - invalidates React Query cache to force re-fetch
  const refreshPermissions = useCallback(async () => {
    if (!isAuthenticated || !user) return;
    await queryClient.invalidateQueries({ queryKey: queryKeys.user.me() });
  }, [isAuthenticated, user, queryClient]);

  // Memoize context value to prevent unnecessary re-renders in consumers
  const value = useMemo<PermissionsContextType>(() => ({
    permissions,
    isLoading,
    error,
    userProfile: userProfile ?? null,
    hasPermission,
    // Admin check - user has admin or owner role
    isAdmin: userProfile?.role === 'admin' || userProfile?.role === 'owner',
    // CRM permissions
    canReadCRM: permissions.crm?.read ?? false,
    canCreateCRM: permissions.crm?.create ?? false,
    canUpdateCRM: permissions.crm?.update ?? false,
    canDeleteCRM: permissions.crm?.delete ?? false,
    // Analytics
    canReadAnalytics: permissions.analytics?.read ?? false,
    // Admin
    canReadAdmin: permissions.admin?.read ?? false,
    canManageUsers: permissions.admin?.manage_users ?? false,
    canManageSettings: permissions.admin?.manage_settings ?? false,
    // Projects permissions
    canReadProjects: permissions.projects?.read ?? false,
    canCreateProjects: permissions.projects?.create ?? false,
    canUpdateProjects: permissions.projects?.update ?? false,
    canDeleteProjects: permissions.projects?.delete ?? false,
    // Hiring permissions
    canReadHiring: permissions.hiring?.read ?? false,
    canCreateHiring: permissions.hiring?.create ?? false,
    canUpdateHiring: permissions.hiring?.update ?? false,
    canDeleteHiring: permissions.hiring?.delete ?? false,
    // Forms permissions
    canReadForms: permissions.forms?.read ?? false,
    canCreateForms: permissions.forms?.create ?? false,
    canUpdateForms: permissions.forms?.update ?? false,
    canDeleteForms: permissions.forms?.delete ?? false,
    // Portal permissions
    canReadPortal: permissions.portal?.read ?? false,
    canManagePortal: permissions.portal?.manage ?? false,
    canPortalStaffAccess: permissions.portal?.staff_access ?? false,
    // Team Hub permissions
    canReadTeamHub: permissions.team_hub?.read ?? false,
    canCreateTeamHub: permissions.team_hub?.create ?? false,
    canUpdateTeamHub: permissions.team_hub?.update ?? false,
    canDeleteTeamHub: permissions.team_hub?.delete ?? false,
    // Customer Success permissions
    canReadCustomerSuccess: permissions.customer_success?.read ?? false,
    canManageCustomerSuccess: permissions.customer_success?.manage ?? false,
    // Production permissions
    canReadProduction: permissions.production?.read ?? false,
    canCreateProduction: permissions.production?.create ?? false,
    canUpdateProduction: permissions.production?.update ?? false,
    canDeleteProduction: permissions.production?.delete ?? false,
    // Agency Intel permissions - default to true for all authenticated users
    canReadAgencyIntel: permissions.agency_intel?.read ?? true,
    // LiquorChat permissions
    canReadLiquorChat: permissions.liquorchat?.read ?? false,
    canManageLiquorChat: permissions.liquorchat?.manage ?? false,
    // Ads permissions
    canReadAds: permissions.ads?.read ?? false,
    canManageAds: permissions.ads?.manage ?? false,
    // Refresh function
    refreshPermissions,
  }), [permissions, isLoading, error, userProfile, hasPermission, refreshPermissions]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}

// Helper function to get default permissions based on role
function getRoleBasedPermissions(role: string): UserPermissions {
  switch (role) {
    case 'owner':
      return {
        crm: { read: true, create: true, update: true, delete: true },
        analytics: { read: true },
        chat: { read: true },
        admin: { read: true, manage_users: true, manage_settings: true },
        projects: { read: true, create: true, update: true, delete: true },
        hiring: { read: true, create: true, update: true, delete: true },
        forms: { read: true, create: true, update: true, delete: true },
        portal: { read: true, manage: true, staff_access: true },
        team_hub: { read: true, create: true, update: true, delete: true },
        customer_success: { read: true, manage: true },
        production: { read: true, create: true, update: true, delete: true },
        agency_intel: { read: true },
        liquorchat: { read: true, manage: true },
        ads: { read: true, manage: true },
      };
    case 'admin':
      return {
        crm: { read: true, create: true, update: true, delete: false },
        analytics: { read: true },
        chat: { read: true },
        admin: { read: true, manage_users: true, manage_settings: false },
        projects: { read: true, create: true, update: true, delete: false },
        hiring: { read: true, create: true, update: true, delete: false },
        forms: { read: true, create: true, update: true, delete: false },
        portal: { read: true, manage: true, staff_access: true },
        team_hub: { read: true, create: true, update: true, delete: false },
        customer_success: { read: true, manage: true },
        production: { read: true, create: true, update: true, delete: false },
        agency_intel: { read: true },
        liquorchat: { read: true, manage: true },
        ads: { read: true, manage: true },
      };
    case 'staff':
      return {
        crm: { read: true, create: false, update: false, delete: false },
        analytics: { read: true },
        chat: { read: true },
        admin: { read: false, manage_users: false, manage_settings: false },
        projects: { read: true, create: true, update: true, delete: false },
        hiring: { read: true, create: false, update: false, delete: false },
        forms: { read: true, create: true, update: true, delete: false },
        portal: { read: true, manage: false, staff_access: false },
        team_hub: { read: true, create: true, update: true, delete: false },
        customer_success: { read: true, manage: false },
        production: { read: true, create: true, update: true, delete: false },
        agency_intel: { read: true },
        liquorchat: { read: true, manage: false },
        ads: { read: false, manage: false },
      };
    case 'readonly':
    default:
      return DEFAULT_PERMISSIONS;
  }
}
